import type { ProjectedSlot } from "./slotProjection";

/**
 * Colour and geometry for the slot preview.
 *
 * Every palette here was run through the six colour checks (lightness band,
 * chroma floor, adjacent CVD separation, normal-vision floor, contrast, and —
 * for the ramps — monotone lightness and step spacing) rather than picked by
 * eye. Changing a value means re-running that validation.
 */

/* ── Categorical: agent identity ───────────────────────────────────────────
   Fixed order, never cycled, so an agent keeps their hue when the roster is
   filtered. Worst adjacent CVD ΔE 8.9 (deutan), normal-vision floor 28.6, all
   steps ≥3:1 against the plate — passing on light and dark surfaces alike. */
export const AGENT_HUES = ["#0080CE", "#B87309", "#7A4FD0", "#2E7D32", "#C43F7C"];

/* ── Ordinal ramps: one hue each, light → dark ─────────────────────────────
   A multi-hue ramp would invent categories the data does not have, so each
   ordered scale gets a single hue and a scale legend. */
export const RAMP_LOAD = ["#8ABAE0", "#6CA6D7", "#4C8FCB", "#2E76B8", "#155C9C", "#04477A"];
export const RAMP_TIME = ["#7FBEBB", "#5FA8A6", "#3F8F90", "#256F74", "#0E5459", "#00393F"];
export const RAMP_DAY = ["#D9AC62", "#C6923C", "#AE7A1E", "#946305", "#784E00", "#5A3A00"];

/* ── Status: reserved ──────────────────────────────────────────────────────
   These mean good / caution / out-of-range and are never reused as a series
   colour in any other mode. */
export const STATUS_HUES = {
  bookable: "#0080CE",
  notice: "#A96A08",
  horizon: "#7488A3",
  past: "#94A3B8",
} as const;

/** Slots that exist but cannot be booked, and inactive scopes. */
export const MUTED_HUE = "#9AA9BC";

/** What the tile colour encodes. */
export type ColorMode = "agent" | "time" | "day" | "status";

export const COLOR_MODES: { value: ColorMode; label: string; kind: "categorical" | "scale" }[] = [
  { value: "agent", label: "Agent", kind: "categorical" },
  { value: "time", label: "Time of day", kind: "scale" },
  { value: "day", label: "Day", kind: "scale" },
  { value: "status", label: "Status", kind: "categorical" },
];

/* ── Lighting ──────────────────────────────────────────────────────────────
   One sun for the whole scene, upper-left. Faces are pre-shaded into rgb()
   strings rather than filtered at runtime: a `filter` would give every one of
   the ~300 tiles its own compositing layer. */
export const SUN_ANGLE_DEG = -35;
const LEFT_FACE_SHADE = 0.82;
const FRONT_FACE_SHADE = 0.68;

export interface TileFaces {
  top: string;
  left: string;
  front: string;
  /** Hue for the cast shadow, tinted so it reads as shadow rather than dirt. */
  shadow: string;
}

function channels(hex: string): [number, number, number] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
}

function shade(hex: string, factor: number): string {
  return `rgb(${channels(hex)
    .map((c) => Math.round(c * factor))
    .join(",")})`;
}

export function tint(hex: string, alpha: number): string {
  return `rgba(${channels(hex).join(",")},${alpha})`;
}

export function facesFor(hex: string): TileFaces {
  return {
    top: hex,
    left: shade(hex, LEFT_FACE_SHADE),
    front: shade(hex, FRONT_FACE_SHADE),
    shadow: hex,
  };
}

/**
 * The bucket a slot belongs to under the current encoding. The legend filters
 * on exactly this key, so colour and interaction can never drift apart.
 */
export function colorKeyFor(
  slot: ProjectedSlot,
  mode: ColorMode,
  ordinal: number,
  rowIndex: number,
  rowCount: number,
): string {
  if (slot.blocked != null) return `status:${slot.blocked === "horizon" ? "horizon" : slot.blocked}`;
  switch (mode) {
    case "agent":
      return slot.userIndex >= 0 ? `agent:${slot.userIndex}` : "agent:0";
    case "time":
      return `time:${rampIndex(rowIndex, rowCount, RAMP_TIME.length)}`;
    case "day":
      return `day:${Math.min(RAMP_DAY.length - 1, slot.dayIndex)}`;
    case "status":
      return "status:bookable";
    default:
      // `ordinal` keeps the signature stable for future encodings that need the
      // slot's position in the horizon-wide sequence.
      return `agent:${ordinal % AGENT_HUES.length}`;
  }
}

function rampIndex(index: number, count: number, steps: number): number {
  if (count <= 1) return 0;
  return Math.min(steps - 1, Math.floor((index / count) * steps));
}

/** Resolves a key produced by {@link colorKeyFor} back to its hue. */
export function hueForKey(key: string, active: boolean): string {
  if (!active) return MUTED_HUE;
  const [family, raw] = key.split(":");
  const n = Number(raw);
  switch (family) {
    case "agent":
      return AGENT_HUES[n % AGENT_HUES.length];
    case "time":
      return RAMP_TIME[Math.min(RAMP_TIME.length - 1, n)];
    case "day":
      return RAMP_DAY[Math.min(RAMP_DAY.length - 1, n)];
    case "status":
      return STATUS_HUES[(raw as keyof typeof STATUS_HUES) in STATUS_HUES
        ? (raw as keyof typeof STATUS_HUES)
        : "bookable"];
    default:
      return AGENT_HUES[0];
  }
}

/** Step from an ordinal ramp for a 0–1 magnitude. */
export function rampStep(ramp: string[], ratio: number): string {
  if (ratio <= 0) return ramp[0];
  return ramp[Math.min(ramp.length - 1, Math.ceil(ratio * ramp.length) - 1)];
}

/** Scene geometry, shared by the 3D stage and the flat views. */
export const SCENE = {
  // Sized for the ~520px preview pane. Too small and the plate floats in the
  // middle of its panel with the tiles unreadable, which is how it first shipped.
  columnWidth: 46,
  columnGap: 9,
  /**
   * Plate that must stay visible between consecutive tiles. Taken out of the
   * tile, never out of the grid — a dense interval loses tile depth rather than
   * losing legibility, which is what made a tight interval read as solid planks.
   */
  minTileGap: 2,
  targetWindowDepth: 310,
  heightPerLead: 10,
  minTileHeight: 9,
  maxTileHeight: 78,
  /** Far rows lift toward the page white — atmospheric perspective. */
  depthFade: 0.26,
  /** Hard cap on rendered tiles; past this the week is aggregated. */
  maxTiles: 300,
  defaultRotateX: 57,
  defaultRotateZ: -42,
  minRotateX: 14,
  maxRotateX: 84,
} as const;

export function pixelsPerMinute(windowSpanMinutes: number): number {
  if (windowSpanMinutes <= 0) return 1;
  return Math.min(2.4, Math.max(0.18, SCENE.targetWindowDepth / windowSpanMinutes));
}
